import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { VersionScanRollup } from '@/lib/apiTypes'
import { useWorkspaceProviders } from '@/lib/hooks'
import { api } from '@/lib/api'
import { useToast } from '@/context/ToastContext'
import { formatDateTime } from '@/lib/format'
import { detectRepoProvider, isSafeRepoUrl } from '@/lib/repoLinks'
import { providerLookupState } from '@/features/providers/lib/providerFreshness'
import { RepoProviderIcon, RegistryIcon } from '@/shared/components/RepoProviderIcon'
import { SeverityDonut } from '@/shared/components/SeverityDonut'
import { Tooltip } from '@/shared/components/Tooltip'

interface WorkspaceProvidersTabProps {
  orgId: string
  workspace: string
}


function getRepoIconAndUrl(url: string | null) {
  if (!url) return { url: null, provider: null, safe: false }
  // `safe` gates whether we render a clickable <a> vs inert text: only https URLs
  // on an allowlisted host become links (defends against legacy/odd-scheme rows).
  const safe = isSafeRepoUrl(url)
  // Sniff the provider only AFTER the gate. detectRepoProvider is a substring match, and this URL
  // comes from an unvalidated X-Git-Repo-URL header, so sniffing first lets
  // `https://evil.com/?x=github.com` wear the GitHub mark and borrow its provenance.
  return { url, provider: safe ? detectRepoProvider(url) : null, safe }
}

/** Permalink to a provider on the Providers page. The registry is part of a provider's identity
 *  there - hashicorp/local can be listed once per registry - so `upstream` is a path segment, and
 *  the lock file's value is the authoritative one. */
function artefactHref(orgId: string, p: { upstream: string; namespace: string; type: string }): string {
  return `/org/${orgId}/providers/${encodeURIComponent(p.upstream)}/${encodeURIComponent(p.namespace)}/${encodeURIComponent(p.type)}`
}

/** A version cell that opens the artefact with that version already expanded. `?v=` is a query
 *  param rather than a path segment because the Providers route keys on the artefact; the
 *  expanded version is seeded state, the same way `?q=` seeds its search box. */
function VersionLink({ orgId, provider, version }: {
  orgId: string
  provider: { upstream: string; namespace: string; type: string }
  version: string
}) {
  return (
    <Link
      to={`${artefactHref(orgId, provider)}?v=${encodeURIComponent(version)}`}
      style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem', color: 'var(--accent)', textDecoration: 'none' }}
      title={`Open ${provider.namespace}/${provider.type} ${version} in Providers`}
    >
      {version}
    </Link>
  )
}

/**
 * The vulnerability cell for one provider version.
 *
 * Three distinct facts, three distinct renderings - collapsing any two of them is the bug this
 * exists to prevent. `SeverityDonut` returns null at zero findings, so a clean version would
 * otherwise render an empty cell, indistinguishable from one that was never scanned.
 */
function VulnCell({ scan }: { scan?: VersionScanRollup | null }) {
  if (!scan || !scan.scanned) {
    return <span style={{ fontSize: '0.75rem', color: 'var(--text3)' }}>not scanned</span>
  }
  if (scan.vuln_count === 0) {
    return <span title="Scanned, no findings" style={{ color: 'var(--green)', fontSize: '0.85rem' }}>✓</span>
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <SeverityDonut
        critical={scan.critical_count}
        high={scan.high_count}
        total={scan.vuln_count}
        unfixableHigh={scan.has_unfixable_high}
        maxCvss={scan.max_cvss}
      />
      {scan.malware_detected && <span className="badge badge-red" style={{ fontSize: '0.6rem' }}>malware</span>}
    </span>
  )
}

/**
 * A repo link that renders a clickable <a> only when `safe`; otherwise inert text
 * (same icon + label, no navigation). Single render path for both call sites so the
 * safe/unsafe branch isn't duplicated. `variant` picks the header vs inline styling.
 */
function RepoLink({ url, provider, safe, label, variant }: {
  url: string
  provider: string | null
  safe: boolean
  label: string
  variant: 'header' | 'inline'
}) {
  const isHeader = variant === 'header'
  const wrapStyle = isHeader
    ? { display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', fontWeight: 500 }
    : { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.75rem' }
  const icon = <RepoProviderIcon provider={provider} size={isHeader ? 18 : 14} />

  if (safe) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" title={url}
         style={{ ...wrapStyle, color: 'var(--accent)', textDecoration: 'none' }}>
        {icon}{label}
      </a>
    )
  }
  return (
    <span title={url} style={{ ...wrapStyle, color: 'var(--text2)' }}>
      {icon}{label}
    </span>
  )
}

export function WorkspaceProvidersTab({ orgId, workspace }: WorkspaceProvidersTabProps) {
  const { data, loading, error, refetch } = useWorkspaceProviders(orgId, workspace)
  const { showToast } = useToast()
  const [rescanning, setRescanning] = useState(false)

  const handleRescan = async () => {
    setRescanning(true)
    try {
      await api.post(`/api/v1/${orgId}/state/${encodeURIComponent(workspace)}/providers/rescan`, {})
      // Awaited INSIDE the try, before `rescanning` clears: otherwise the button re-enables while
      // the refetch is still in flight and a second click queues a second rescan.
      await refetch()
      // The lookup is queued, not done - the row still reads "checking..." on purpose, so say so
      // rather than leaving the user staring at unchanged version strings.
      showToast('Provider lookup queued - versions will update shortly', 'success')
    } catch (err) {
      showToast((err as Error).message ?? 'Rescan failed', 'error')
    } finally {
      setRescanning(false)
    }
  }

  if (loading) {
    return (
      <div className="empty-state">
        <div className="spinner" />
        <p style={{ marginTop: 8, fontSize: '0.85rem', color: 'var(--text2)' }}>Loading providers...</p>
      </div>
    )
  }

  // `!data` matters: useAsync deliberately keeps the last-good payload when a REFETCH fails, so
  // checking `error` alone would replace a fully-rendered table with an error panel (and no retry)
  // the first time a post-rescan refetch blipped. With data in hand the error is a banner instead.
  if (error && !data) {
    return (
      <div style={{ padding: 24 }}>
        <div className="feedback-error">
          <p style={{ fontWeight: 500, marginBottom: 4 }}>Error loading providers</p>
          <p style={{ fontSize: '0.8rem' }}>{error}</p>
        </div>
      </div>
    )
  }

  const { lock_file, providers, truncated } = data || { lock_file: null, providers: [], truncated: false }

  if (!lock_file) {
    return (
      <div style={{ padding: 24 }}>
        <h3 style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>Upload Terraform Lock File</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--text2)', marginBottom: 12 }}>
          Upload your <code style={{ background: 'var(--bg2)', padding: '2px 5px', borderRadius: 3, fontFamily: 'var(--mono)' }}>terraform.lock.hcl</code> file to track provider versions and updates.
        </p>
        <pre className="code-block" style={{ marginBottom: 16 }}>{`# GitHub Actions
curl -X PUT \\
  -u "terramantle-bot:$TF_HTTP_PASSWORD" \\
  https://registry.terramantle.dev/state/${orgId}/${workspace}/terraform.lock.hcl \\
  --data-binary @.terraform.lock.hcl

# GitLab CI
curl -X PUT \\
  -u "terramantle-bot:$TF_HTTP_PASSWORD" \\
  https://registry.terramantle.dev/state/${orgId}/${workspace}/terraform.lock.hcl \\
  --data-binary @.terraform.lock.hcl`}</pre>
        <p style={{ fontSize: '0.75rem', color: 'var(--text3)' }}>
          The <code style={{ background: 'var(--bg2)', padding: '2px 5px', borderRadius: 3, fontFamily: 'var(--mono)' }}>X-Git-Repo-URL</code> header is optional and will be used to link to your Terraform code repository.
        </p>
      </div>
    )
  }

  const infraRepo = getRepoIconAndUrl(lock_file.infrastructure_repo_url)

  return (
    <div style={{ padding: 24 }}>
      <p style={{ fontSize: '0.78rem', color: 'var(--text3)', marginBottom: 16, lineHeight: 1.5 }}>
        The providers consumed by this workspace's Terraform state, parsed from the uploaded lock file.
      </p>
      {error && (
        <div className="feedback-error" style={{ marginBottom: 16 }}>
          <p style={{ fontSize: '0.8rem' }}>Could not refresh providers: {error}. Showing the last known data.</p>
        </div>
      )}
      {truncated && (
        <div style={{
          marginBottom: 16, padding: '8px 12px', borderRadius: 4,
          border: '1px solid var(--amber)', color: 'var(--amber)', fontSize: '0.8rem',
        }}>
          This lock file holds more providers than can be shown at once; the list below is partial.
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {infraRepo.url && (
            <RepoLink
              url={infraRepo.url}
              provider={infraRepo.provider}
              safe={infraRepo.safe}
              label={infraRepo.safe ? 'View infrastructure repo' : 'Infrastructure repo'}
              variant="header"
            />
          )}
        </div>
        <button
          onClick={handleRescan}
          disabled={rescanning}
          className="btn btn-ghost btn-sm"
          style={{ opacity: rescanning ? 0.5 : 1 }}
        >
          {rescanning ? '🔄 Rescanning...' : '🔄 Rescan'}
        </button>
      </div>

      {providers.length === 0 ? (
        <div className="empty-state fu">No providers found in lock file</div>
      ) : (
        <div className="table-wrap fu">
          <table>
            <thead>
              <tr>
                <th>
                  <Tooltip text="The provider from this workspace's lock file, with its registry and source repo." placement="top">
                    <span tabIndex={0} className="cursor-help">Provider</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Version this workspace has locked right now." placement="top">
                    <span tabIndex={0} className="cursor-help">Current</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Version range the configuration declares for this provider." placement="top">
                    <span tabIndex={0} className="cursor-help">Constraint</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Newest version available upstream." placement="top">
                    <span tabIndex={0} className="cursor-help">Latest</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Vulnerabilities in the locked version's last completed scan." placement="top">
                    <span tabIndex={0} className="cursor-help">In use</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Vulnerabilities in the latest version - what upgrading would leave you with." placement="top">
                    <span tabIndex={0} className="cursor-help">If upgraded</span>
                  </Tooltip>
                </th>
              </tr>
            </thead>
            <tbody>
              {providers.map((provider) => {
                const latestAvailable = provider.latest_version
                // This column is about latest_version, so it asks only whether the upstream
                // lookup ever succeeded - NOT providerFreshness, which is scoped by the lock
                // file's constraint and would answer a different question here. Shared with the
                // graph's edge modal so the two surfaces cannot drift on what amber means.
                const lookup = providerLookupState(provider)

                const providerRepo = getRepoIconAndUrl(provider.provider_repo_url)

                return (
                  <tr key={provider.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{ display: 'inline-flex', color: 'var(--text2)' }}
                          title={provider.upstream === 'opentofu.org' ? 'registry.opentofu.org' : 'registry.terraform.io'}
                        >
                          <RegistryIcon upstream={provider.upstream} size={18} />
                        </span>
                        <div>
                          <div style={{ fontWeight: 500, marginBottom: 4 }}>
                            <Link
                              to={artefactHref(orgId, provider)}
                              style={{ color: 'var(--accent)', textDecoration: 'none' }}
                              title={`Open ${provider.namespace}/${provider.type} in Providers`}
                            >
                              {provider.namespace}/{provider.type}
                            </Link>
                          </div>
                          {providerRepo.url && (
                            <RepoLink
                              url={providerRepo.url}
                              provider={providerRepo.provider}
                              safe={providerRepo.safe}
                              label="Provider repo"
                              variant="inline"
                            />
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <VersionLink orgId={orgId} provider={provider} version={provider.current_version} />
                    </td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text2)' }}>
                      {provider.constraints || '-'}
                    </td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem' }}>
                      {latestAvailable ? (
                        <VersionLink orgId={orgId} provider={provider} version={latestAvailable} />
                      ) : lookup === 'unavailable' ? (
                        <span className="badge badge-amber">upstream unavailable</span>
                      ) : lookup === 'pending' ? (
                        <span className="dim">checking…</span>
                      ) : (
                        <span className="dim">-</span>
                      )}
                    </td>
                    <td style={{ fontSize: '0.8rem' }}>
                      <VulnCell scan={provider.current_scan} />
                    </td>
                    <td style={{ fontSize: '0.8rem' }}>
                      {/* No newer version (or already on it) => no upgrade to describe. Two
                          identical rings side by side would read as a rendering bug. */}
                      {!provider.latest_version || provider.latest_version === provider.current_version
                        ? <span style={{ fontSize: '0.75rem', color: 'var(--text3)' }}>up to date</span>
                        : <VulnCell scan={provider.latest_scan} />}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 12, fontSize: '0.75rem', color: 'var(--text3)' }}>
        <p>
          Uploaded {formatDateTime(lock_file.uploaded_at)}
          {lock_file.uploaded_by ? ` by ${lock_file.uploaded_by}` : ''}
        </p>
      </div>
    </div>
  )
}
